import { getApiUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CosmicBackground from '../components/CosmicBackground';

export default function ResetPassword() {
  const BACKEND_URL = getApiUrl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
      <CosmicBackground />
      
      <motion.div
        className="relative z-10 glass rounded-2xl p-8 w-full max-w-md mx-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {success ? (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-starlight mb-2">Password Reset!</h2>
            <p className="text-starlight/70 mb-6">
              Your password has been successfully reset.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-etheric/40 hover:bg-etheric/60"
            >
              Return to Login
            </Button>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl text-starlight mb-2 text-center">
              Reset Password
            </h2>
            <p className="text-starlight/60 text-sm mb-6 text-center">
              Enter your new password below
            </p>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-starlight/50" />
                  <Input
                    type="password"
                    placeholder="New password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-black/30 border-white/10 text-starlight"
                    required
                    minLength={8}
                    disabled={!token}
                  />
                </div>
                <p className="text-starlight/40 text-xs mt-1">Minimum 8 characters</p>
              </div>
              
              <div className="mb-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-starlight/50" />
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-black/30 border-white/10 text-starlight"
                    required
                    minLength={8}
                    disabled={!token}
                  />
                </div>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-rose-400 text-sm mb-4">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <Button
                type="submit"
                disabled={isLoading || !token}
                className="w-full bg-etheric/40 hover:bg-etheric/60 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-starlight/50 hover:text-starlight text-sm"
              >
                ← Back to login
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
